/** Normalize GET /api/trailers/locations payloads to a flat slot list. */

const norm = (s) => String(s ?? "").trim().toLowerCase();

// Only strip a leading noise-word when something remains afterward — a section/bay/dock
// literally named just "Section"/"Bay"/"Dock" (a real, seen-in-the-wild name) must not
// collapse to "", which would make slotMatchesSelection skip that field's check entirely.
function stripLeadingIfNonEmpty(s, re) {
  const stripped = s.replace(re, "");
  return stripped.trim() ? stripped : s;
}

/** Strip common noise so "Bay 1" matches "bay1" and dock numbers align. */
function normComparable(s) {
  let out = norm(s);
  out = stripLeadingIfNonEmpty(out, /^section\s*/i);
  out = stripLeadingIfNonEmpty(out, /^bay\s*/i);
  out = stripLeadingIfNonEmpty(out, /^dock\s*/i);
  out = stripLeadingIfNonEmpty(out, /^parking\s+bay\s*/i);
  return out.replace(/\s+/g, " ");
}

function labelsLooselyEqual(selected, slotVal) {
  const a = normComparable(selected);
  const b = normComparable(slotVal);
  if (!a || !b) return false;
  if (a === b) return true;
  const compact = (s) => s.replace(/\s+/g, "");
  if (compact(a) === compact(b)) return true;
  if (/^\d+$/.test(a) && /^\d+$/.test(b) && Number(a) === Number(b)) return true;
  // Avoid "1" matching "10"; allow substring only for longer tokens.
  if (a.length >= 3 && b.includes(a)) return true;
  if (b.length >= 3 && a.includes(b)) return true;
  return false;
}

/** Parse composite labels such as "secA - bay1". */
export function parseSlotLabel(label) {
  const out = { sectionName: "", bayName: "", dockName: "" };
  const text = String(label ?? "").trim();
  if (!text) return out;

  const dashParts = text.split(/\s*[-–]\s*/);
  if (dashParts.length >= 2) {
    out.sectionName = dashParts[0].trim();
    const slotPart = dashParts.slice(1).join(" - ").trim();
    if (/^dock/i.test(slotPart)) {
      out.dockName = slotPart.replace(/^dock\s*/i, "").trim();
    } else if (/^bay/i.test(slotPart)) {
      out.bayName = slotPart.replace(/^bay\s*/i, "").trim();
    } else {
      out.bayName = slotPart;
    }
    return out;
  }

  return out;
}

function slotType(slot) {
  return String(slot?.type ?? slot?.slotType ?? "").trim().toUpperCase();
}

function isSlotLike(obj) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return false;
  const type = slotType(obj);
  if (type === "BAY" || type === "DOCK") return true;
  if (obj.occupancyStatus != null && (obj.name || obj.label)) return true;
  if (obj.sectionName && (obj.name || obj.bayName || obj.dockName)) return true;
  return false;
}

function withSlotContext(slot, ctx) {
  const parsed = parseSlotLabel(slot?.label);
  return {
    ...slot,
    officeCode: slot.officeCode ?? slot.office_code ?? ctx.officeCode,
    sectionName:
      slot.sectionName ??
      slot.section ??
      slot.sectionLabel ??
      parsed.sectionName ??
      ctx.sectionName,
    warehouseName: slot.warehouseName ?? slot.warehouse ?? ctx.warehouseName,
  };
}

function flattenLocationSlots(raw, ctx = {}, depth = 0) {
  if (raw == null || depth > 8) return [];

  if (Array.isArray(raw)) {
    return raw.flatMap((item) => flattenLocationSlots(item, ctx, depth + 1));
  }

  if (typeof raw !== "object") return [];

  if (isSlotLike(raw)) {
    return [withSlotContext(raw, ctx)];
  }

  const nextCtx = {
    officeCode: raw.officeCode ?? raw.office_code ?? ctx.officeCode,
    sectionName:
      raw.sectionName ??
      raw.section ??
      raw.sectionLabel ??
      raw.name ??
      ctx.sectionName,
    warehouseName: raw.warehouseName ?? raw.warehouse ?? ctx.warehouseName,
  };

  let out = [];
  const nestedKeys = [
    "locations",
    "slots",
    "items",
    "data",
    "content",
    "bays",
    "docks",
    "yardLocations",
    "rows",
    "results",
    "values",
    "children",
  ];

  for (const key of nestedKeys) {
    if (raw[key] != null) {
      out = out.concat(flattenLocationSlots(raw[key], nextCtx, depth + 1));
    }
  }

  if (Array.isArray(raw.sections)) {
    for (const section of raw.sections) {
      const sectionCtx = {
        ...nextCtx,
        sectionName:
          section?.sectionName ??
          section?.section ??
          section?.name ??
          nextCtx.sectionName,
        sectionId: section?.sectionId ?? section?.section_id ?? section?.id,
      };
      out = out.concat(
        flattenLocationSlots(section?.bays ?? section?.bayList, sectionCtx, depth + 1)
      );
      out = out.concat(
        flattenLocationSlots(section?.docks ?? section?.dockList, sectionCtx, depth + 1)
      );
      out = out.concat(flattenLocationSlots(section?.slots, sectionCtx, depth + 1));
      if (isSlotLike(section)) {
        out = out.concat(flattenLocationSlots(section, sectionCtx, depth + 1));
      }
    }
  }

  return out;
}

export function extractTrailerLocationSlots(raw) {
  return flattenLocationSlots(raw);
}

function slotSection(slot) {
  if (!slot) return undefined;
  const parsed = parseSlotLabel(slot.label);
  return (
    slot.sectionName ??
    slot.section ??
    slot.sectionLabel ??
    slot.yardSection ??
    slot.sectionNameLabel ??
    slot.yardSectionName ??
    slot.sectionDisplayName ??
    parsed.sectionName
  );
}

function slotBay(slot) {
  if (slotType(slot) === "DOCK") return undefined;
  const parsed = parseSlotLabel(slot?.label);
  return (
    slot.bayName ??
    slot.bayLabel ??
    slot.name ??
    slot.bay ??
    slot.parkingBay ??
    slot.parkingBayName ??
    slot.bayDisplayName ??
    slot.bayNumber ??
    parsed.bayName ??
    (slot.bay && typeof slot.bay === "object"
      ? slot.bay.name ?? slot.bay.bayName ?? slot.bay.label
      : undefined)
  );
}

function slotDock(slot) {
  if (slotType(slot) === "BAY") return undefined;
  const parsed = parseSlotLabel(slot?.label);
  return (
    slot.dockName ??
    slot.dockLabel ??
    slot.name ??
    slot.dock ??
    slot.dockNumber ??
    slot.dockDisplayName ??
    parsed.dockName ??
    (slot.dock && typeof slot.dock === "object"
      ? slot.dock.name ?? slot.dock.dockName ?? slot.dock.dockNumber
      : undefined)
  );
}

function slotIds(slot, selection = {}) {
  const type = slotType(slot);
  const genericId = slot.id ?? slot.slotId;
  const wantsBay = Boolean(selection.bayName) && !selection.dockName;
  const wantsDock = Boolean(selection.dockName) && !selection.bayName;

  const bid =
    slot.currentBayId ??
    slot.bayId ??
    slot.bay_id ??
    slot.yardBayId ??
    slot.yardBayID ??
    (slot.bay && typeof slot.bay === "object"
      ? slot.bay.id ?? slot.bay.bayId ?? slot.bay.currentBayId
      : undefined) ??
    (type === "BAY" && genericId != null ? genericId : undefined) ??
    (wantsBay && genericId != null ? genericId : undefined);
  const did =
    slot.currentDockId ??
    slot.dockId ??
    slot.dock_id ??
    slot.yardDockId ??
    (slot.dock && typeof slot.dock === "object"
      ? slot.dock.id ?? slot.dock.dockId ?? slot.dock.currentDockId
      : undefined) ??
    (type === "DOCK" && genericId != null ? genericId : undefined) ??
    (wantsDock && genericId != null ? genericId : undefined);
  return { bid, did };
}

export function locationSlotKey(sectionName, slotTypeName, slotName) {
  return `${norm(sectionName)}|${String(slotTypeName ?? "").toUpperCase()}|${normComparable(slotName)}`;
}

/** Fast lookup map for section + bay/dock → API slot row. */
export function buildLocationSlotIndex(slots) {
  const index = new Map();
  for (const slot of slots || []) {
    if (!slot || typeof slot !== "object") continue;
    const sectionName = String(slotSection(slot) ?? "").trim();
    if (!sectionName) continue;

    const type = slotType(slot);
    const bayName = slotBay(slot);
    const dockName = slotDock(slot);

    if (bayName && type !== "DOCK") {
      index.set(locationSlotKey(sectionName, "BAY", bayName), slot);
    }
    if (dockName && type !== "BAY") {
      index.set(locationSlotKey(sectionName, "DOCK", dockName), slot);
    }
  }
  return index;
}

/**
 * Match yard-settings section/bay/dock labels to API slots carrying bay/dock IDs.
 */
function slotMatchesSelection(slot, { sectionName, bayName, dockName }, { allowMissingSection }) {
  const type = slotType(slot);
  const sn = sectionName ? normComparable(sectionName) : null;
  const bn = bayName ? normComparable(bayName) : null;
  const dn = dockName ? normComparable(dockName) : null;

  if (bn && !dn && type === "DOCK") return false;
  if (dn && !bn && type === "BAY") return false;

  const slotSecRaw = slotSection(slot);
  const slotSec = normComparable(slotSecRaw);

  if (sn) {
    if (slotSec) {
      if (!labelsLooselyEqual(sectionName, slotSecRaw)) return false;
    } else if (!allowMissingSection) {
      return false;
    }
  }

  if (bn) {
    const slotBayRaw = slotBay(slot);
    if (!slotBayRaw || !labelsLooselyEqual(bayName, slotBayRaw)) return false;
  }

  if (dn) {
    const slotDockRaw = slotDock(slot);
    if (!slotDockRaw || !labelsLooselyEqual(dockName, slotDockRaw)) return false;
  }

  return true;
}

function pickFirstMatching(slots, selection, opts) {
  for (const slot of slots) {
    if (!slotMatchesSelection(slot, selection, opts)) continue;
    const { bid, did } = slotIds(slot, selection);
    return {
      currentBayId: bid,
      currentDockId: did,
      slot,
    };
  }
  return null;
}

function pickFirstMatchingSectionId(slots, { sectionId, bayName, dockName }) {
  const sid = sectionId != null && sectionId !== "" ? String(sectionId) : "";
  if (!sid) return null;
  const bn = bayName ? normComparable(bayName) : null;
  const dn = dockName ? normComparable(dockName) : null;

  for (const slot of slots) {
    const slotSectionId = String(
      slot.sectionId ?? slot.section_id ?? slot.yardSectionId ?? ""
    );
    if (!slotSectionId || slotSectionId !== sid) continue;

    const type = slotType(slot);
    if (bn && !dn && type === "DOCK") continue;
    if (dn && !bn && type === "BAY") continue;

    if (bn) {
      const slotBayRaw = slotBay(slot);
      if (!slotBayRaw || !labelsLooselyEqual(bayName, slotBayRaw)) continue;
    }
    if (dn) {
      const slotDockRaw = slotDock(slot);
      if (!slotDockRaw || !labelsLooselyEqual(dockName, slotDockRaw)) continue;
    }

    const { bid, did } = slotIds(slot, { bayName, dockName });
    return {
      currentBayId: bid,
      currentDockId: did,
      slot,
    };
  }
  return null;
}

/**
 * Match yard-settings section/bay/dock labels to API slots carrying currentBayId / currentDockId.
 */
export function matchSlotForYardSelection(slots, { sectionName, bayName, dockName, sectionId }) {
  if (!Array.isArray(slots) || !slots.length) {
    return { currentBayId: null, currentDockId: null, slot: null };
  }

  const selection = { sectionName, bayName, dockName };

  const strict = pickFirstMatching(slots, selection, { allowMissingSection: false });
  if (strict) return strict;

  const relaxedSection = pickFirstMatching(slots, selection, { allowMissingSection: true });
  if (relaxedSection) return relaxedSection;

  const bySectionId = pickFirstMatchingSectionId(slots, { sectionId, bayName, dockName });
  if (bySectionId) return bySectionId;

  // Yard-settings section labels often differ from /api/trailers/locations section names.
  if (bayName && !dockName) {
    const bayOnly = pickFirstMatching(
      slots,
      { sectionName: "", bayName, dockName: "" },
      { allowMissingSection: true }
    );
    if (bayOnly) return bayOnly;
  }

  if (dockName && !bayName) {
    const dockOnly = pickFirstMatching(
      slots,
      { sectionName: "", bayName: "", dockName },
      { allowMissingSection: true }
    );
    if (dockOnly) return dockOnly;
  }

  return { currentBayId: null, currentDockId: null, slot: null };
}

function toNumericId(value) {
  if (value == null || value === "") return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

/** Primary keys from GET /api/trailers/locations — backend validates `id` + `type`. */
function resolveLocationSpot(slot, fallbackType) {
  if (!slot) return { spotId: undefined, spotType: undefined };

  const type = slotType(slot);
  const resolvedType =
    type === "BAY" || type === "DOCK" ? type : fallbackType;

  const { bid, did } = slotIds(slot, {
    bayName: resolvedType === "BAY" ? "_" : "",
    dockName: resolvedType === "DOCK" ? "_" : "",
  });
  if (resolvedType === "BAY") {
    const bayId = toNumericId(bid);
    if (bayId != null) return { spotId: bayId, spotType: "BAY" };
  }
  if (resolvedType === "DOCK") {
    const dockId = toNumericId(did);
    if (dockId != null) return { spotId: dockId, spotType: "DOCK" };
  }

  const spotId = toNumericId(slot.id ?? slot.slotId);
  if (spotId != null && resolvedType) {
    return { spotId, spotType: resolvedType };
  }

  return { spotId: undefined, spotType: undefined };
}

/**
 * Resolve bay/dock primary keys from GET /api/trailers/locations for POST /api/trailers/check-in.
 * Matches bay and dock independently (never reuses a display label as an id).
 * Prefers selectedSpotId + selectedSpotType; also emits legacy currentBayId / currentDockId when distinct.
 */
export function resolveCheckInLocationPayload(slots, { sectionName, bayName, dockName, sectionId }) {
  const empty = {
    selectedSpotId: undefined,
    selectedSpotType: undefined,
    currentBayId: undefined,
    currentDockId: undefined,
    baySlot: null,
    dockSlot: null,
  };

  if (!Array.isArray(slots) || !slots.length) return empty;

  const bayMatch = bayName
    ? matchSlotForYardSelection(slots, { sectionName, bayName, dockName: "", sectionId })
    : { currentBayId: null, currentDockId: null, slot: null };
  const dockMatch = dockName
    ? matchSlotForYardSelection(slots, { sectionName, bayName: "", dockName, sectionId })
    : { currentBayId: null, currentDockId: null, slot: null };

  const baySpot = resolveLocationSpot(bayMatch.slot, "BAY");
  const dockSpot = resolveLocationSpot(dockMatch.slot, "DOCK");

  let currentBayId = baySpot.spotType === "BAY" ? baySpot.spotId : undefined;
  let currentDockId = dockSpot.spotType === "DOCK" ? dockSpot.spotId : undefined;

  if (
    currentBayId != null &&
    currentDockId != null &&
    currentBayId === currentDockId
  ) {
    if (dockName && !bayName) {
      currentBayId = undefined;
    } else if (bayName && !dockName) {
      currentDockId = undefined;
    } else if (dockName) {
      currentBayId = undefined;
    } else {
      currentDockId = undefined;
    }
  }

  let selectedSpotId;
  let selectedSpotType;
  if (dockName && currentDockId != null) {
    selectedSpotId = currentDockId;
    selectedSpotType = "DOCK";
  } else if (bayName && currentBayId != null) {
    selectedSpotId = currentBayId;
    selectedSpotType = "BAY";
  }

  return {
    selectedSpotId,
    selectedSpotType,
    currentBayId,
    currentDockId,
    baySlot: bayMatch.slot,
    dockSlot: dockMatch.slot,
  };
}

/** Keep slots for the selected warehouse when the API returns multiple warehouses. */
export function filterLocationSlotsByWarehouse(slots, warehouseName, warehouseRef = "") {
  if (!Array.isArray(slots) || !slots.length) return [];
  const name = String(warehouseName ?? "").trim();
  const ref = String(warehouseRef ?? "").trim();
  if (!name && !ref) return slots;

  const scoped = slots.filter((slot) => {
    const slotName = String(slot.warehouseName ?? slot.warehouse ?? "").trim();
    const slotRef = String(
      slot.warehouseRef ?? slot.warehouse_ref ?? slot.locationRef ?? ""
    ).trim();
    if (ref && slotRef && ref.toLowerCase() === slotRef.toLowerCase()) return true;
    if (name && slotName && labelsLooselyEqual(name, slotName)) return true;
    if (!slotName && !slotRef) return true;
    return false;
  });

  return scoped.length ? scoped : slots;
}

/** Keep slots for the active warehouse office when the API returns multiple offices. */
export function filterLocationSlotsByOfficeCode(slots, officeCode, { strict = false } = {}) {
  if (!Array.isArray(slots) || !slots.length) return [];
  const target = norm(officeCode);
  if (!target) return slots;
  const scoped = slots.filter((slot) => {
    const slotOffice = norm(slot?.officeCode ?? slot?.office_code ?? "");
    return strict ? slotOffice === target : !slotOffice || slotOffice === target;
  });
  if (strict) return scoped;
  return scoped.length ? scoped : slots;
}

/** Human-readable occupancy / reservation hints for UI tooltips. */
export function formatLocationSlotHint(slot) {
  if (!slot || typeof slot !== "object") return "";
  const parts = [];
  const occ = slot.occupancyStatus ?? slot.occupancy ?? "";
  if (occ) parts.push(String(occ));
  if (slot.reservedUntil) parts.push(`Reserved until ${slot.reservedUntil}`);
  if (slot.reservationStartsWithinTwoHours) parts.push("Reservation starting soon");
  if (slot.reservationMismatchWarning) parts.push(String(slot.reservationMismatchWarning));
  return parts.join(" · ");
}

